import {CommonModule} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, NgModule, provideAppInitializer} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatRippleModule} from '@angular/material/core';
import {MatDialogModule} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {BrowserModule} from '@angular/platform-browser';
import {RouterModule} from '@angular/router';
import {BreakPoint, BREAKPOINT, FlexLayoutModule} from '@ngbracket/ngx-layout';
import {provideTranslateService, TranslateCompiler, TranslatePipe} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {ApiModule, Configuration, FrontendConfig} from '@npck/auth-frontend-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    addSentryErrorLog,
    AppConfigService,
    CommonsModule,
    customBreakPoints,
    ROUTER_REPLACE_BY_DEFAULT,
    SentryInterceptor,
    SentryService,
    TracingInterceptor,
    TracingService,
} from '@npck/libs/commons';
import {UiKitModule} from '@npck/libs/ui-kit';
import {ApiModule as OauthServerApiModule, Configuration as OauthServerConfiguration} from '@npck/oauth-service-api';
import {CodeInputModule} from 'angular-code-input';
import {QRCodeComponent} from 'angularx-qrcode';
import {ClipboardModule} from 'ngx-clipboard';
import {NgxMaskDirective, NgxMaskPipe, provideNgxMask} from 'ngx-mask';
import {TranslateMessageFormatCompiler} from 'ngx-translate-messageformat-compiler';
import {provideNgxWebstorage, withLocalStorage, withSessionStorage} from 'ngx-webstorage';
import versionConfig from '../assets/config/version.json';
import type {FaceTecSDK as FaceTecSDKType} from '../assets/facetec/core-sdk/FaceTecSDK.js/FaceTecSDK';
import {appRoutes} from './app-routing.module';
import {ESignDialogComponent} from './components/e-sign-dialog/e-sign-dialog.component';
import {ExpandCardComponent} from './components/expand-card/expand-card.component';
import {OauthErrorComponent} from './components/error/oauth-error.component';
import {LivenessComponent} from './components/liveness/liveness.component';
import {AuthHolderService} from './domain/auth-holder.service';
import {AppErrorHandler} from './infra/app-error-handler.service';
import {AuthLayoutComponent} from './layout/auth/auth.layout.component';
import {BinComponent} from './pages/bin/bin.component';
import {CodeComponent} from './pages/code/code.component';
import {ConsentComponent} from './pages/consent/consent.component';
import {DocumentComponent} from './pages/document/document.component';
import {ErrorRedirectComponent} from './pages/error-redirect/error-redirect.component';
import {ErrorComponent} from './pages/error/error.component';
import {FinishComponent} from './pages/finish-page/finish.component';
import {IinComponent} from './pages/iin/iin.component';
import {LivenessCreateQrComponent} from './pages/liveness-device/liveness-create-qr/liveness-create-qr.component';
import {LivenessDeviceComponent} from './pages/liveness-device/liveness-device.component';
import {LivenessRetryComponent} from './pages/liveness-device/liveness-retry/liveness-retry.component';
import {OauthAuthorizeComponent} from './pages/oauth/oauth-authorize.component';
import {OauthComponent} from './pages/oauth/oauth.component';
import {LivenessQrComponent} from './pages/qr/liveness-qr/liveness-qr.component';
import {QrInfoComponent} from './pages/qr/qr-info/qr-info.component';
import {ResetEsignComponent} from './pages/reset-esign/reset-esign.component';
import {RevokeEsignComponent} from './pages/revoke-esign/revoke-esign.component';
import {AppComponent} from './root/app.component';
import {trimProviderIds} from './utils/scope.utils';

const DESKTOP_TO_MOBILE_BREAKPOINT = 600;

const apiConfigurationFactory = (oauthHolderService: AuthHolderService): Configuration => new Configuration({
    basePath: '',
    credentials: {
        FirstFactorAuth: () => oauthHolderService.firstFactorToken,
        LivenessQrStatusAuth: () => oauthHolderService.firstFactorToken,
        SecondFactorAuth: () => oauthHolderService.secondFactorToken,
        OrganizationAuth: () => oauthHolderService.organizationToken,
        AuthSessionAuth: () => oauthHolderService.getActiveAuthToken(),
        FinishedAuth: () => oauthHolderService.authSessionToken,
        AllStepAuth: () => oauthHolderService.getAnyToken(),
    },
});

const apiOauthConfigurationFactory = (oauthHolderService: AuthHolderService): OauthServerConfiguration =>
    new OauthServerConfiguration({
        basePath: '',
        credentials: {
            BearerTokenAuth: () => oauthHolderService.authSessionToken,
        },
    });

function addSentryErrorLogFactory(message: string): Promise<void> {
    return new Promise<void>((resolve) => {
        addSentryErrorLog(message);
        resolve();
    });
}

export function initApp(
    appConfigService: AppConfigService,
    analyticsService: AnalyticsService,
    authHolderService: AuthHolderService,
    tracingService: TracingService,
    sentryService: SentryService,
): () => Promise<any> {
    return () => addSentryErrorLogFactory('Init app started')
        .then(() => appConfigService.init({...versionConfig, enableBackendConfig: true}))
        .then(() => addSentryErrorLogFactory('appConfigService init finished'))
        .then(() => tracingService.init(span => {
            const authSessionData = authHolderService.authSessionData;
            span.setAttribute('sessionId', authHolderService.oauthSessionId);
            span.setAttribute('clientId', authSessionData?.clientId);
            span.setAttribute('scopes', trimProviderIds(authSessionData?.scopes));
            span.setAttribute('redirectUri', authSessionData?.redirectUri);
            span.setAttribute('state', authSessionData?.stateEncrypted);
            span.setAttribute('phone', authSessionData?.phoneEncrypted);
            span.setAttribute('language', authSessionData?.language);
            span.setAttribute('biometryVendor', authSessionData?.biometryVendor);
            span.setAttribute('hasOrganizationAuth', authSessionData?.hasOrganizationAuth);
            span.setAttribute('iin', authSessionData?.iinEncrypted);
            span.setAttribute('deviceKey', authHolderService.deviceKey);
            span.setAttribute('organizationBin', authHolderService.organization?.binEncrypted);
            span.setAttribute('livenessQrId', authHolderService.livenessQrId);
            span.setAttribute('clientTime', new Date().toString());
        }))
        .then(() => addSentryErrorLogFactory('tracingService init finished'))
        .then(() => analyticsService.init(
            'AUTH',
            () => {
                const authSessionData = authHolderService.authSessionData;
                return {
                    sessionId: authHolderService.oauthSessionId,
                    firstLogin: authSessionData?.firstLogin,
                    client_id: authSessionData?.clientId,
                    client_name: authSessionData?.clientName,
                    redirect_uri: authSessionData?.redirectUri,
                    scopes: trimProviderIds(authSessionData?.scopes),
                    language: authSessionData?.language,
                    phone: authSessionData?.phoneEncrypted,
                    iin: authSessionData?.iinEncrypted,
                    traceId: tracingService.getTraceId(),
                    bin: authHolderService.organization?.binEncrypted,
                    withoutOtp: authSessionData?.skipSms,
                };
            }))
        .then(() => addSentryErrorLogFactory('analyticsService init finished'))
        .then(() => sentryService.init({
            tagsFactory: () => {
                const authSessionData = authHolderService.authSessionData;
                return {
                    'sessionId': authHolderService.oauthSessionId,
                    'clientId': authSessionData?.clientId,
                    'scopes': authSessionData?.scopes?.join(','),
                    'redirectUri': authSessionData?.redirectUri,
                    'state': authSessionData?.stateEncrypted,
                    'phone': authSessionData?.phoneEncrypted,
                    'iin': authSessionData?.iinEncrypted,
                    'language': authSessionData?.language,
                    'biometryVendor': authSessionData?.biometryVendor,
                    'hasOrganizationAuth': authSessionData?.hasOrganizationAuth,
                    'deviceKey': authHolderService.deviceKey,
                    'organizationBin': authHolderService.organization?.binEncrypted,
                    'livenessQrId': authHolderService.livenessQrId,
                    'openTelemetryTraceId': tracingService.getTraceId(),
                    'clientTime': new Date().toString(),
                };
            },
            mutedEventsByTags: [
                [{'errorCode': 'access_denied'}, {'errorDescription': 'Oauth2 error [access_denied]'}],
            ],
            groupsByTags: {
                'network': [
                    [{'httpStatusText': 'Unknown Error'}, {'httpStatus': '0'}],
                    [{'url': 'liveness'}],
                ],
            },
        }))
        .then(() => addSentryErrorLogFactory('sentryService init finished'));
}

export function breakPointsProviderFactory(): BreakPoint[] {
    addSentryErrorLog('breakPointsProviderFactory init started');
    return customBreakPoints(DESKTOP_TO_MOBILE_BREAKPOINT);
}

export const httpInterceptorProviders = [
    {provide: HTTP_INTERCEPTORS, useClass: TracingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SentryInterceptor, multi: true},
];

@NgModule({
    declarations: [
        AppComponent,
        OauthComponent,
        OauthAuthorizeComponent,
        AuthLayoutComponent,
        CodeComponent,
        IinComponent,
        ConsentComponent,
        OauthErrorComponent,
        ErrorComponent,
        ErrorRedirectComponent,
        BinComponent,
        LivenessCreateQrComponent,
        QrInfoComponent,
        DocumentComponent,
        ESignDialogComponent,
        LivenessQrComponent,
        LivenessDeviceComponent,
        LivenessRetryComponent,
        LivenessComponent,
        FinishComponent,
        RevokeEsignComponent,
        ResetEsignComponent
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        CommonsModule,
        BrowserModule,
        RouterModule.forRoot(appRoutes),
        ApiModule,
        OauthServerApiModule,
        FlexLayoutModule,
        MatButtonModule,
        MatInputModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        FormsModule,
        ReactiveFormsModule,
        CodeInputModule,
        NgxMaskPipe,
        UiKitModule,
        MatRippleModule,
        MatProgressBarModule,
        QRCodeComponent,
        MatDialogModule,
        ClipboardModule,
        TranslatePipe,
        ExpandCardComponent,
    ],
    providers: [
        provideAppInitializer(() => {
            return (initApp)(
                inject(AppConfigService),
                inject(AnalyticsService),
                inject(AuthHolderService),
                inject(TracingService),
                inject(SentryService))();
        }),
        provideNgxMask(),
        provideNgxWebstorage(
            withLocalStorage(),
            withSessionStorage(),
        ),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json',
            }),
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler,
            },
        }),
        {provide: BREAKPOINT, useFactory: breakPointsProviderFactory, multi: true},
        {provide: ROUTER_REPLACE_BY_DEFAULT, useValue: true},
        {provide: Configuration, useFactory: apiConfigurationFactory, deps: [AuthHolderService]},
        {provide: OauthServerConfiguration, useFactory: apiOauthConfigurationFactory, deps: [AuthHolderService]},
        {provide: ErrorHandler, useClass: AppErrorHandler},
        httpInterceptorProviders,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {
}

declare global {
    const FaceTecSDK: typeof FaceTecSDKType;

    interface Window {
        appConfig: FrontendConfig;
    }
}
